/**
 * Score metadata — the dimensions, what each range means, thresholds, and the
 * overall Alignment Score derived from them. Single source of truth for the
 * result UI (tooltips, bands, feedback).
 *
 * Each dimension:
 *   key, label, blurb           — name + one-line definition
 *   higherBetter                — direction
 *   bands: [[threshold,color]]  — color, most-significant first
 *   ranges: [{label, meaning}]  — what each band means in plain words (hover/click)
 *   advice                      — what to do when it's low
 */

export const DIMENSIONS = [
  {
    key: 'relevance',
    label: 'Relevance',
    blurb: 'Does the output actually address the request? LLM-judged when a judge model is available (works for prose, code, SQL, JSON alike); otherwise query-vs-output embedding similarity.',
    higherBetter: true,
    bands: [[0.75, 'good'], [0.5, 'warn'], [0, 'bad']],
    ranges: [
      { label: '≥ 0.75 · Solid', meaning: 'Directly answers the question.' },
      { label: '0.50–0.75 · Partial', meaning: 'Vague or tangential: only partly on point.' },
      { label: '< 0.50 · Off-topic', meaning: 'Misses what was asked.' },
    ],
    advice: 'Answer drifts from the question: tighten the prompt or give the model what it needs to answer directly.',
  },
  {
    key: 'correctness',
    label: 'Correctness',
    blurb: 'Is the output factually and logically correct? For code/SQL, would it run and produce the right result? (LLM-judged: shown only when a judge model is available.)',
    higherBetter: true,
    bands: [[0.75, 'good'], [0.5, 'warn'], [0, 'bad']],
    ranges: [
      { label: '≥ 0.75 · Correct', meaning: 'Accurate and sound.' },
      { label: '0.50–0.75 · Flawed', meaning: 'Partly right: has mistakes or gaps.' },
      { label: '< 0.50 · Wrong', meaning: 'Materially incorrect.' },
    ],
    advice: 'Output has substantive errors: give the model the facts/schema it needs and constrain it to them.',
  },
  {
    key: 'groundedness',
    label: 'Grounded',
    blurb: 'Is the output supported by the provided context, with no fabrication? LLM-judged when available; otherwise context embedding similarity + NLI entailment.',
    higherBetter: true,
    bands: [[0.7, 'good'], [0.45, 'warn'], [0, 'bad']],
    ranges: [
      { label: '≥ 0.70 · Grounded', meaning: 'Well supported by the context.' },
      { label: '0.45–0.70 · Loose', meaning: 'Partly supported: some unsupported claims.' },
      { label: '< 0.45 · Ungrounded', meaning: 'Largely unsupported: hallucination risk.' },
    ],
    advice: 'Output is weakly supported by the context: add/clarify the context or instruct the model to stick to it.',
  },
  {
    key: 'entity_fidelity',
    label: 'Entities',
    blurb: 'Fraction of hard facts (IDs, numbers, $ amounts) from the input that survive into the output.',
    higherBetter: true,
    bands: [[0.999, 'good'], [0.6, 'warn'], [0, 'bad']],
    ranges: [
      { label: '= 1.0 · All kept', meaning: 'Every identifier/number was carried through.' },
      { label: '0.6–1.0 · Some dropped', meaning: 'A few facts were lost or altered.' },
      { label: '< 0.6 · Most dropped', meaning: 'Key IDs/numbers are missing or wrong.' },
    ],
    advice: 'Model is dropping IDs/numbers from the input: tell it to preserve all identifiers and amounts verbatim.',
  },
  {
    key: 'adherence',
    label: 'Adherence',
    blurb: 'Did it follow the system instructions and rules (format, style, constraints)? LLM-judged when available; otherwise limited to mechanically-checkable rules (JSON format, length caps, required/forbidden phrases).',
    higherBetter: true,
    bands: [[0.999, 'good'], [0.6, 'warn'], [0, 'bad']],
    ranges: [
      { label: '= 1.0 · Follows rules', meaning: 'All checkable system rules satisfied.' },
      { label: '0.6–1.0 · Partial', meaning: 'Some rules followed, some broken.' },
      { label: '< 0.6 · Ignores rules', meaning: 'Violates the format/length/forbidden rules.' },
    ],
    advice: 'Output breaks checkable system rules: restate the format/length/constraints explicitly and up front.',
  },
  {
    key: 'contradiction',
    label: 'Contradiction',
    blurb: 'Does the output logically contradict the context? (NLI cross-encoder). Lower is better.',
    higherBetter: false,
    bands: [[0.6, 'bad'], [0.3, 'warn'], [0, 'good']],
    ranges: [
      { label: '< 0.30 · Consistent', meaning: 'Agrees with the context.' },
      { label: '0.30–0.60 · Tension', meaning: 'Some statements sit awkwardly with the context.' },
      { label: '≥ 0.60 · Contradicts', meaning: 'Directly argues against the context (logical reversal).' },
    ],
    advice: 'Output conflicts with the context: check for reversed logic (e.g. "is" vs "is not").',
  },
  {
    key: 'refusal',
    label: 'Refusal',
    blurb: 'Did the run punt or crash instead of completing? (refusal regex + failure-centroid proximity). Lower is better.',
    higherBetter: false,
    bands: [[0.7, 'bad'], [0.4, 'warn'], [0, 'good']],
    ranges: [
      { label: '< 0.40 · Completed', meaning: 'A real answer was produced.' },
      { label: '0.40–0.70 · Hedged', meaning: 'Partial refusal / heavy hedging.' },
      { label: '≥ 0.70 · Refused', meaning: 'Canned refusal or error instead of an answer.' },
    ],
    advice: 'Model refused or errored: check the request, the key, and rate/token limits.',
  },
  {
    key: 'stability',
    label: 'Stability',
    blurb: 'Self-consistency across repeated runs at the same temperature. (only with stability runs > 1)',
    higherBetter: true,
    bands: [[0.85, 'good'], [0.6, 'warn'], [0, 'bad']],
    ranges: [
      { label: '≥ 0.85 · Stable', meaning: 'Predictable: reruns agree.' },
      { label: '0.60–0.85 · Variable', meaning: 'Noticeable variation between runs.' },
      { label: '< 0.60 · Erratic', meaning: 'Outputs swing widely: risky in production.' },
    ],
    advice: 'Outputs vary between runs: lower the temperature or constrain the prompt before relying on it.',
  },
];

export const ALIGNMENT = {
  key: 'composite',
  label: 'Alignment Score',
  blurb: 'Overall alignment of the output with the intent: a weighted blend of the dimensions below (relevance, correctness, grounded, entities, non-contradiction, adherence), renormalized over whichever are available, with refusals heavily penalized.',
  higherBetter: true,
  bands: [[0.7, 'good'], [0.45, 'warn'], [0, 'bad']],
  ranges: [
    { label: '≥ 0.70 · Well aligned', meaning: 'Solid: safe to rely on for this input.' },
    { label: '0.45–0.70 · Needs work', meaning: 'Usable but has weak dimensions to fix.' },
    { label: '< 0.45 · Poor', meaning: 'Not aligned: address the flagged dimensions.' },
  ],
};

// Comparative metric across models (only meaningful with 2+ models).
export const CROSS_MODEL = {
  key: 'divergence',
  label: 'Cross-model divergence',
  blurb: "How much the models disagree on this input. It's the mean pairwise cosine distance between the models' output embeddings: 0 means the answers are near-identical, higher means they diverge. The model furthest from the consensus is flagged as the outlier (which may be uniquely wrong OR uniquely better).",
  higherBetter: null,
  ranges: [
    { label: '~0.00–0.10 · Agree', meaning: 'Models produced essentially the same answer: model choice barely matters here.' },
    { label: '0.10–0.30 · Some spread', meaning: 'Noticeable differences in wording or approach.' },
    { label: '> 0.30 · Disagree', meaning: 'Models answer quite differently: this input is model-sensitive; inspect the outlier.' },
  ],
};

// Back-compat: some callers import SCORE_DEFS (dimensions + the overall).
export const SCORE_DEFS = [...DIMENSIONS, ALIGNMENT];

// Text colours only: a score is read as a number, not as a coloured badge.
// Theme tokens, so each band is legible on both the light and dark surface.
export const COLOR_VARS = {
  good: { fg: 'var(--feedback-success)', bg: 'transparent' },
  warn: { fg: 'var(--feedback-warning)', bg: 'transparent' },
  bad: { fg: 'var(--feedback-error)', bg: 'transparent' },
  muted: { fg: 'var(--text-muted)', bg: 'transparent' },
};

export function bandColor(def, value) {
  if (value == null || Number.isNaN(value)) return 'muted';
  for (const [thresh, color] of def.bands) {
    if (value >= thresh) return color;
  }
  return 'muted';
}

/** Short qualitative status word for a dimension value: ok / low / extremely low / —. */
export function statusFor(def, value) {
  const c = bandColor(def, value);
  if (c === 'good') return 'ok';
  if (c === 'warn') return 'low';
  if (c === 'bad') return 'extremely low';
  return null;
}

export function alignmentLabel(value) {
  const c = bandColor(ALIGNMENT, value);
  if (c === 'good') return 'Well aligned';
  if (c === 'warn') return 'Needs work';
  if (c === 'bad') return 'Poor';
  return '—';
}

/**
 * Actionable feedback for one model's scores: which dimensions are low /
 * extremely low and what to do. Extremely-low first, then low. Also notes
 * dimensions that weren't computed.
 */
export function buildFeedback(scores, degraded = []) {
  const items = [];
  for (const d of DIMENSIONS) {
    const v = scores[d.key];
    if (v == null) continue;
    const c = bandColor(d, v);
    if (c === 'bad') items.push({ severity: 'bad', label: d.label, status: 'extremely low', message: d.advice });
    else if (c === 'warn') items.push({ severity: 'warn', label: d.label, status: 'low', message: d.advice });
  }
  items.sort((a, b) => (a.severity === 'bad' ? 0 : 1) - (b.severity === 'bad' ? 0 : 1));

  const missing = DIMENSIONS.filter((d) => scores[d.key] == null).map((d) => d.label);
  return { items, missing };
}

export function fmtScore(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(2);
}

export function fmtCost(usd) {
  if (usd == null) return '—';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

export function fmtLatency(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
